import {
  addToast,
  Button,
  OutlinedSelectOptionType,
  Table,
} from '@octano/global-ui';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col } from 'reactstrap';
import TableEmptyContent from '../../../../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../../../../config/routes';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { copySurvey, updateSurveyStatus } from '../../../api';
import { Survey, SurveyStatus, surveyStatusTexts } from '../../../types/Survey';
import useColumns from '../hooks/useColumns';
import ConfirmModal, { ConfirmModalRef } from './ConfirmModal';
import {
  ITEMS_PER_PAGE_DEFAULT,
  useSurveyListLoader,
} from './SurveyListLoader';

type ConfirmAction = 'copy' | 'status';

export default function SurveyTable() {
  const { data, loading, refresh, updateQuery, query } = useSurveyListLoader();
  const history = useHistory();
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);

  const confirmRef = useRef<ConfirmModalRef>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>('status');
  const [confirmMessage, setConfirmMessage] = useState<string>('');

  const handleChangeStatus = async (row: Survey, newStatus: SurveyStatus) => {
    try {
      const resp = await updateSurveyStatus(row.id, { status: newStatus });
      if (resp.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('surveyList.statusChange.error'),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t('surveyList.statusChange.success'),
      });
      refresh();
    } catch {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('surveyList.statusChange.error'),
      });
    }
  };

  const handleCopySurvey = async (row: Survey) => {
    try {
      const resp = await copySurvey(row.id);
      if (resp.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('surveyList.copy.published.error'),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t('surveyList.copy.published.success'),
      });
      history.push(`${PathsLayouts.surveys}/draft`);
      return;
    } catch {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('surveyList.copy.published.error'),
      });
    }
  };

  const onConfirmModal = (data: any) => {
    if (confirmAction === 'status') {
      handleChangeStatus(data.row, data.newStatus);
    } else if (confirmAction === 'copy') {
      handleCopySurvey(data.row);
    }
  };

  const statusOptions = useMemo<OutlinedSelectOptionType[]>(
    () =>
      Object.entries(surveyStatusTexts).map(([value, label]) => ({
        icon:
          value === SurveyStatus.Published
            ? { name: 'eye', color: 'primary' }
            : { name: 'check', color: 'success' },
        value,
        label,
      })),
    [],
  );

  const columnsOthers = useColumns({
    onClickEdit: (row) => {
      history.push(`/surveys/update/published/${row.id}`);
    },
    onClickCopy: (row) => {
      setConfirmAction('copy');
      setConfirmMessage(t('surveyList.copy.published.confirmation'));
      confirmRef.current?.openModal({ row });
    },
    statusOptions,
    onChangeStatus: (row, newStatus) => {
      setConfirmAction('status');
      setConfirmMessage(t('surveyList.statusChange.message'));
      confirmRef.current?.openModal({ row, newStatus });
    },
  });

  return (
    <>
      <Card className="m-3 p-3">
        <div className="mb-3">
          <p className="fs-16 text-light mt-3">
            {t('surveyList.descriptionText')}
          </p>
        </div>

        <div className="mb-5">
          <div className="d-flex flex-wrap w-100 align-items-center">
            <Button
              text={t('surveyList.addSurvey')}
              color="primary"
              icon="plus"
              size="md"
              className="mb-3"
              style={{ minWidth: '254px' }}
              onClick={() => history.push('/surveys/create')}
            />
          </div>
          <Table
            columns={columnsOthers}
            data={data?.data ?? []}
            isLoadingResults={loading}
            loadingView={{
              title: t('surveyList.loadingText'),
              subtitle: t('surveyList.loadingSubText'),
            }}
            noResultsText={
              <TableEmptyContent
                title={t('surveyList.emptyText')}
                subtitle={t('surveyList.emptySubText')}
              />
            }
            pagination={{
              currentPage: query.page,
              totalItems: data?.total ?? 0,
              totalPages: data?.total_pages ?? 0,
              itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
              onChangePage: (page: number) => updateQuery({ ...query, page }),
            }}
            footComponent={() => <Col style={{ minHeight: '70px' }} />}
          />
        </div>
      </Card>

      <ConfirmModal
        ref={confirmRef}
        message={confirmMessage}
        onConfirm={onConfirmModal}
        onDecline={() => {}}
      />
    </>
  );
}
